/**
 * Firebase Analytics for tracking menu clicks, comparable searches, and CEA number.
 * Set REACT_APP_FIREBASE_* in .env to enable. CEA is stored as user property "cea_number"
 * so you can segment by user in Analytics.
 */
import { initializeApp, FirebaseApp } from 'firebase/app';
import { isDevelopment } from '../../config/env';
import {
  getAnalytics,
  Analytics,
  logEvent as firebaseLogEvent,
  setUserProperties,
} from 'firebase/analytics';

const PROJECT_ID = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'transformation-office';
const apiKey = process.env.REACT_APP_FIREBASE_API_KEY;
const appId = process.env.REACT_APP_FIREBASE_APP_ID;
const measurementId = process.env.REACT_APP_FIREBASE_MEASUREMENT_ID;

const firebaseConfig = apiKey && appId
  ? {
      apiKey,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || `${PROJECT_ID}.firebaseapp.com`,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || PROJECT_ID,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || `${PROJECT_ID}.appspot.com`,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
      appId,
      ...(measurementId ? { measurementId } : {}),
    }
  : null;

let app: FirebaseApp | null = null;
let analytics: Analytics | null = null;
/**
 * Cached copy of the CEA number that was last passed to `setAnalyticsCea`.
 * Firebase only exposes user-properties as write-only, so we keep our own
 * copy here for the rare event payloads (e.g. `share_link_created`) where
 * we want CEA on the *event* in addition to the user property — that lets
 * us join share invites back to the issuing agent without depending on
 * Analytics user-property reporting.
 */
let cachedCeaNumber: string | null = null;

export function initAnalytics(): void {
  if (app || !firebaseConfig) return;
  try {
    app = initializeApp(firebaseConfig);
    analytics = getAnalytics(app);
  } catch (e) {
    if (isDevelopment) {
      console.warn('Analytics init skipped', e);
    }
  }
}

function safeLogEvent(name: string, params?: Record<string, string | number | boolean>): void {
  if (!analytics) return;
  try {
    if (isDevelopment) {
      const ceaValue = typeof params?.cea_number === 'string' ? params.cea_number.trim() : '';
      console.info('[analytics]', name, params || {});
      if (!ceaValue) {
        console.warn('[analytics] missing cea_number', {
          event: name,
          params: params || {},
        });
      }
    }
    firebaseLogEvent(analytics, name, params as Record<string, unknown>);
  } catch (e) {
    if (isDevelopment) {
      console.warn('Analytics logEvent failed', name, e);
    }
  }
}

/**
 * Set CEA number as user property so Analytics reports can be segmented by user.
 * Call when the user is known (e.g. after login or CEA param).
 */
export function setAnalyticsCea(ceaNumber: string | null | undefined): void {
  const trimmed = ceaNumber?.trim();
  if (!trimmed) return;
  // Always cache, even if Firebase analytics isn't initialised — share-link
  // events fall back to the cached value so we don't lose CEA tagging when
  // Firebase config is absent (e.g. local dev without REACT_APP_FIREBASE_*).
  cachedCeaNumber = trimmed;
  if (!analytics) return;
  try {
    setUserProperties(analytics, { cea_number: trimmed });
  } catch (e) {
    if (isDevelopment) {
      console.warn('Analytics setUserProperties failed', e);
    }
  }
}

/**
 * Read the most recent CEA number that `setAnalyticsCea` was called with.
 * Used by events (currently `share_link_created`) that need CEA on the
 * event payload itself.
 */
export function getCachedAnalyticsCea(): string | null {
  return cachedCeaNumber;
}

/**
 * Log the authenticated user landing on the Super Picker main page.
 */
export function logMainPageViewed(ceaNumber?: string | null): void {
  safeLogEvent('main_page_viewed', {
    page_name: 'super_picker_home',
    ...(ceaNumber ? { cea_number: ceaNumber } : {}),
  });
}

/**
 * Log a menu / tab click. Use menu_item like "Notifications", "Shortlisted Units", "Find Comparable Tool", "Tab: Client Name".
 */
export function logMenuClick(menuItem: string, ceaNumber?: string | null): void {
  safeLogEvent('menu_click', {
    menu_item: menuItem,
    ...(ceaNumber ? { cea_number: ceaNumber } : {}),
  });
}

export function logProjectTrainingClick(
  projectId?: string | number | null,
  ceaNumber?: string | null
): void {
  safeLogEvent('project_training_click', {
    ...(projectId !== undefined && projectId !== null ? { project_id: String(projectId) } : {}),
    ...(ceaNumber ? { cea_number: ceaNumber } : {}),
  });
}

export function logRegisterForTrainingClick(ceaNumber?: string | null): void {
  safeLogEvent('register_for_training_click', {
    ...(ceaNumber ? { cea_number: ceaNumber } : {}),
  });
}

export function logConfirmTrainingRegistrationClick(
  eventId?: string | number | null,
  ceaNumber?: string | null
): void {
  safeLogEvent('confirm_training_registration_click', {
    ...(eventId !== undefined && eventId !== null ? { event_id: String(eventId) } : {}),
    ...(ceaNumber ? { cea_number: ceaNumber } : {}),
  });
}

/**
 * Log a comparable-units search. project_name and unit_number are included so you can see what each user searched.
 */
export function logComparableSearch(
  projectName: string,
  unitNumber: string,
  ceaNumber?: string | null
): void {
  safeLogEvent('comparable_search', {
    project_name: projectName,
    unit_number: unitNumber,
    ...(ceaNumber ? { cea_number: ceaNumber } : {}),
  });
}

export function logProjectResearchTabViewed(
  projectName?: string | null,
  ceaNumber?: string | null
): void {
  safeLogEvent('project_research_tab_viewed', {
    tab_name: 'project_research',
    ...(projectName ? { project_name: projectName } : {}),
    ...(ceaNumber ? { cea_number: ceaNumber } : {}),
  });
}

type ProjectResearchSearchFiltersInput = {
  priceMin?: string | null;
  priceMax?: string | null;
  psfMin?: string | null;
  psfMax?: string | null;
  sizeMin?: string | null;
  sizeMax?: string | null;
  floorMin?: string | null;
  floorMax?: string | null;
  bathMin?: string | null;
  bathMax?: string | null;
  districts?: string[] | null;
  regions?: string[] | null;
  tenure?: string[] | null;
  projectCategories?: string[] | null;
  topYearMin?: string | null;
  topYearMax?: string | null;
  bedroomMin?: string | null;
  bedroomMax?: string | null;
  locationLat?: string | null;
  locationLng?: string | null;
  locationLabel?: string | null;
  radiusKm?: string | null;
};

type ProjectResearchResultProjectInput = {
  searchId: string;
  rank: number;
  projectId?: string | number | null;
  projectName?: string | null;
  projectCategory?: string | null;
  district?: string | null;
  region?: string | null;
  tenure?: string | null;
  matchedUnitCount?: number | null;
  distanceFromReferenceKm?: number | null;
  ceaNumber?: string | null;
};

const compactCsv = (items?: Array<string | number | null | undefined> | null, maxLength = 500): string | undefined => {
  const value = (items ?? [])
    .map((item) => String(item ?? '').trim())
    .filter(Boolean)
    .join(',');
  if (!value) return undefined;
  return value.length > maxLength ? `${value.slice(0, maxLength - 3)}...` : value;
};

const compactString = (value?: string | number | null, maxLength = 500): string | undefined => {
  const text = String(value ?? '').trim();
  if (!text) return undefined;
  return text.length > maxLength ? `${text.slice(0, maxLength - 3)}...` : text;
};

function toProjectResearchFilterParams(
  filters: ProjectResearchSearchFiltersInput,
): Record<string, string | number | boolean> {
  const districts = filters.districts ?? [];
  const regions = filters.regions ?? [];
  const tenure = filters.tenure ?? [];
  const projectCategories = filters.projectCategories ?? [];
  const hasLocation = Boolean(filters.locationLat && filters.locationLng);

  return {
    ...(filters.priceMin ? { price_min: filters.priceMin } : {}),
    ...(filters.priceMax ? { price_max: filters.priceMax } : {}),
    ...(filters.psfMin ? { psf_min: filters.psfMin } : {}),
    ...(filters.psfMax ? { psf_max: filters.psfMax } : {}),
    ...(filters.sizeMin ? { size_min: filters.sizeMin } : {}),
    ...(filters.sizeMax ? { size_max: filters.sizeMax } : {}),
    ...(filters.floorMin ? { floor_min: filters.floorMin } : {}),
    ...(filters.floorMax ? { floor_max: filters.floorMax } : {}),
    ...(filters.bathMin ? { bath_min: filters.bathMin } : {}),
    ...(filters.bathMax ? { bath_max: filters.bathMax } : {}),
    ...(filters.bedroomMin ? { bedroom_min: filters.bedroomMin } : {}),
    ...(filters.bedroomMax ? { bedroom_max: filters.bedroomMax } : {}),
    ...(filters.topYearMin ? { top_year_min: filters.topYearMin } : {}),
    ...(filters.topYearMax ? { top_year_max: filters.topYearMax } : {}),
    district_count: districts.length,
    region_count: regions.length,
    tenure_count: tenure.length,
    project_category_count: projectCategories.length,
    ...(compactCsv(districts) ? { districts: compactCsv(districts) as string } : {}),
    ...(compactCsv(regions) ? { regions: compactCsv(regions) as string } : {}),
    ...(compactCsv(tenure) ? { tenure: compactCsv(tenure) as string } : {}),
    ...(compactCsv(projectCategories) ? { project_categories: compactCsv(projectCategories) as string } : {}),
    has_location: hasLocation,
    ...(compactString(filters.locationLabel) ? { location_label: compactString(filters.locationLabel) as string } : {}),
    ...(filters.radiusKm ? { radius_km: filters.radiusKm } : {}),
  };
}

export function logProjectResearchSearchSubmitted(input: {
  searchId: string;
  viewMode: 'project' | 'unit';
  filters: ProjectResearchSearchFiltersInput;
  ceaNumber?: string | null;
}): void {
  safeLogEvent('project_research_search_submitted', {
    search_id: input.searchId,
    view_mode: input.viewMode,
    ...toProjectResearchFilterParams(input.filters),
    ...(input.ceaNumber ? { cea_number: input.ceaNumber } : {}),
  });
}

export function logProjectResearchSearchResults(input: {
  searchId: string;
  viewMode: 'project' | 'unit';
  projectCount: number;
  totalMatchedUnits: number;
  loadedUnitCount?: number;
  projectNames?: string[];
  ceaNumber?: string | null;
}): void {
  safeLogEvent('project_research_search_results', {
    search_id: input.searchId,
    view_mode: input.viewMode,
    project_count: input.projectCount,
    total_matched_units: input.totalMatchedUnits,
    ...(input.loadedUnitCount !== undefined ? { loaded_unit_count: input.loadedUnitCount } : {}),
    ...(compactCsv(input.projectNames) ? { result_project_names: compactCsv(input.projectNames) as string } : {}),
    ...(input.ceaNumber ? { cea_number: input.ceaNumber } : {}),
  });
}

export function logProjectResearchResultProject(input: ProjectResearchResultProjectInput): void {
  safeLogEvent('project_research_result_project', {
    search_id: input.searchId,
    rank: input.rank,
    ...(input.projectId !== undefined && input.projectId !== null ? { project_id: String(input.projectId) } : {}),
    ...(input.projectName ? { project_name: input.projectName } : {}),
    ...(input.projectCategory ? { project_category: input.projectCategory } : {}),
    ...(input.district ? { district: input.district } : {}),
    ...(input.region ? { region: input.region } : {}),
    ...(input.tenure ? { tenure: input.tenure } : {}),
    ...(input.matchedUnitCount !== undefined && input.matchedUnitCount !== null ? { matched_unit_count: input.matchedUnitCount } : {}),
    ...(input.distanceFromReferenceKm !== undefined && input.distanceFromReferenceKm !== null ? { distance_from_reference_km: input.distanceFromReferenceKm } : {}),
    ...(input.ceaNumber ? { cea_number: input.ceaNumber } : {}),
  });
}

export function logProjectResearchViewModeChanged(input: {
  viewMode: 'project' | 'unit';
  ceaNumber?: string | null;
}): void {
  safeLogEvent('project_research_view_mode_changed', {
    view_mode: input.viewMode,
    ...(input.ceaNumber ? { cea_number: input.ceaNumber } : {}),
  });
}

export function logProjectResearchProjectExpanded(input: {
  projectName: string;
  bucket: string;
  count: number;
  ceaNumber?: string | null;
}): void {
  safeLogEvent('project_research_project_expanded', {
    project_name: input.projectName,
    bucket: input.bucket,
    count: input.count,
    ...(input.ceaNumber ? { cea_number: input.ceaNumber } : {}),
  });
}

export function logProjectResearchSortChanged(input: {
  viewMode: 'project' | 'unit';
  column: string;
  direction: string;
  ceaNumber?: string | null;
}): void {
  safeLogEvent('project_research_sort_changed', {
    view_mode: input.viewMode,
    column: input.column,
    direction: input.direction,
    ...(input.ceaNumber ? { cea_number: input.ceaNumber } : {}),
  });
}

export function logProjectResearchFilterChanged(input: {
  viewMode: 'project' | 'unit';
  column: string;
  filterType: 'text' | 'range' | 'category';
  value?: string | string[] | null;
  bound?: 'min' | 'max';
  ceaNumber?: string | null;
}): void {
  const value = Array.isArray(input.value)
    ? compactCsv(input.value)
    : compactString(input.value);
  safeLogEvent('project_research_filter_changed', {
    view_mode: input.viewMode,
    column: input.column,
    filter_type: input.filterType,
    ...(input.bound ? { bound: input.bound } : {}),
    ...(value ? { value } : {}),
    ...(Array.isArray(input.value) ? { value_count: input.value.length } : {}),
    ...(input.ceaNumber ? { cea_number: input.ceaNumber } : {}),
  });
}

export function logProjectResearchExport(input: {
  format: 'pdf' | 'csv';
  viewMode: 'project' | 'unit';
  recordCount: number;
  visibleColumnCount?: number;
  ceaNumber?: string | null;
}): void {
  safeLogEvent('project_research_export', {
    format: input.format,
    view_mode: input.viewMode,
    record_count: input.recordCount,
    ...(input.visibleColumnCount !== undefined ? { visible_column_count: input.visibleColumnCount } : {}),
    ...(input.ceaNumber ? { cea_number: input.ceaNumber } : {}),
  });
}

type UnitTrackingInput = {
  projectName?: string | null;
  projectId?: string | number | null;
  unitNumber?: string | null;
  unitId?: string | number | null;
  source: string;
  ceaNumber?: string | null;
};

function toTrackingBaseParams(input: UnitTrackingInput): Record<string, string | number | boolean> {
  return {
    source: input.source,
    ...(input.projectName ? { project_name: input.projectName } : {}),
    ...(input.projectId !== undefined && input.projectId !== null ? { project_id: String(input.projectId) } : {}),
    ...(input.unitNumber ? { unit_number: input.unitNumber } : {}),
    ...(input.unitId !== undefined && input.unitId !== null ? { unit_id: String(input.unitId) } : {}),
    ...(input.ceaNumber ? { cea_number: input.ceaNumber } : {}),
  };
}

type ThumbsupUnitTrackingInput = UnitTrackingInput & {
  bedroomNumber?: number | null;
  sizeSqft?: string | number | null;
  thumbsupCount?: number | null;
  isLive?: number | boolean | null;
};

type PricePromoUnitTrackingInput = UnitTrackingInput & {
  section?: string | null;
  bedroomNumber?: number | null;
  sizeSqft?: string | number | null;
  price?: number | null;
  pricePsf?: number | null;
  priceChange?: number | null;
  priceChangePercent?: number | null;
  priceChangeAt?: string | null;
  isBestStackBedroom?: boolean | null;
  isLive?: number | boolean | null;
};

function withUnitDetailParams(
  base: Record<string, string | number | boolean>,
  input: {
    bedroomNumber?: number | null;
    sizeSqft?: string | number | null;
    isLive?: number | boolean | null;
  }
): Record<string, string | number | boolean> {
  return {
    ...base,
    ...(input.bedroomNumber !== undefined && input.bedroomNumber !== null ? { bedroom_number: input.bedroomNumber } : {}),
    ...(input.sizeSqft !== undefined && input.sizeSqft !== null ? { size_sqft: input.sizeSqft } : {}),
    ...(input.isLive !== undefined && input.isLive !== null ? { is_live: typeof input.isLive === 'boolean' ? input.isLive : input.isLive === 1 } : {}),
  };
}

export function logProjectViewed(
  projectName: string,
  source: string,
  ceaNumber?: string | null,
  projectId?: string | number | null
): void {
  const params = toTrackingBaseParams({ projectName, projectId, source, ceaNumber });
  if (isDevelopment) {
    console.info('[analytics] project_viewed', params);
    if (!projectName || !projectName.trim()) {
      console.warn('[analytics] project_viewed missing project_name', {
        source,
        projectId,
        ceaNumber,
      });
    }
  }
  safeLogEvent('project_viewed', params);
}

export function logReferenceUnitSet(input: UnitTrackingInput): void {
  safeLogEvent('reference_unit_set', toTrackingBaseParams(input));
}

export function logCompareAdd(input: UnitTrackingInput): void {
  safeLogEvent('compare_add', toTrackingBaseParams(input));
}

export function logFloorPlanOpened(input: UnitTrackingInput): void {
  safeLogEvent('floor_plan_opened', toTrackingBaseParams(input));
}

export function logFloorPlan3dOpened(input: UnitTrackingInput & { mode: '3d' | '3ds' }): void {
  const { mode, ...unit } = input;
  safeLogEvent('floor_plan_3d_opened', { ...toTrackingBaseParams(unit), mode });
}

export function logFloorPlanComparisonOpened(input: UnitTrackingInput): void {
  safeLogEvent('floor_plan_comparison_opened', toTrackingBaseParams(input));
}

export function logShortlistAdd(input: UnitTrackingInput): void {
  safeLogEvent('shortlist_add', toTrackingBaseParams(input));
}

export function logShortlistRemove(input: UnitTrackingInput): void {
  safeLogEvent('shortlist_remove', toTrackingBaseParams(input));
}

export function logTowerViewOpened(input: UnitTrackingInput): void {
  safeLogEvent('tower_view_opened', toTrackingBaseParams(input));
}

export function logSitePlanOpened(
  projectName: string,
  source: string,
  ceaNumber?: string | null,
  projectId?: string | number | null
): void {
  safeLogEvent('site_plan_opened', toTrackingBaseParams({ projectName, projectId, source, ceaNumber }));
}

export function logSitePlanComparisonOpened(input: UnitTrackingInput): void {
  safeLogEvent('site_plan_comparison_opened', toTrackingBaseParams(input));
}

export function logThumbsupUnitsOpened(input: {
  source: string;
  totalProjects?: number;
  totalUnits?: number;
  hasLastViewed?: boolean;
  ceaNumber?: string | null;
}): void {
  safeLogEvent('thumbsup_units_opened', {
    source: input.source,
    ...(input.totalProjects !== undefined ? { total_projects: input.totalProjects } : {}),
    ...(input.totalUnits !== undefined ? { total_units: input.totalUnits } : {}),
    ...(input.hasLastViewed !== undefined ? { has_last_viewed: input.hasLastViewed } : {}),
    ...(input.ceaNumber ? { cea_number: input.ceaNumber } : {}),
  });
}

export function logThumbsupUnitSelected(input: ThumbsupUnitTrackingInput): void {
  safeLogEvent(
    'thumbsup_unit_selected',
    {
      ...withUnitDetailParams(toTrackingBaseParams(input), input),
      ...(input.thumbsupCount !== undefined && input.thumbsupCount !== null ? { thumbsup_count: input.thumbsupCount } : {}),
    }
  );
}

export function logPricePromoUnitsOpened(input: {
  source: string;
  totalProjects?: number;
  totalUnits?: number;
  latestCount?: number;
  topSavingCount?: number;
  hasRecentPromo?: boolean;
  ceaNumber?: string | null;
}): void {
  safeLogEvent('price_promo_units_opened', {
    source: input.source,
    ...(input.totalProjects !== undefined ? { total_projects: input.totalProjects } : {}),
    ...(input.totalUnits !== undefined ? { total_units: input.totalUnits } : {}),
    ...(input.latestCount !== undefined ? { latest_count: input.latestCount } : {}),
    ...(input.topSavingCount !== undefined ? { top_saving_count: input.topSavingCount } : {}),
    ...(input.hasRecentPromo !== undefined ? { has_recent_promo: input.hasRecentPromo } : {}),
    ...(input.ceaNumber ? { cea_number: input.ceaNumber } : {}),
  });
}

export function logPricePromoUnitSelected(input: PricePromoUnitTrackingInput): void {
  safeLogEvent(
    'price_promo_unit_selected',
    {
      ...withUnitDetailParams(toTrackingBaseParams(input), input),
      ...(input.section ? { section: input.section } : {}),
      ...(input.price !== undefined && input.price !== null ? { price: input.price } : {}),
      ...(input.pricePsf !== undefined && input.pricePsf !== null ? { price_psf: input.pricePsf } : {}),
      ...(input.priceChange !== undefined && input.priceChange !== null ? { price_change: input.priceChange } : {}),
      ...(input.priceChangePercent !== undefined && input.priceChangePercent !== null ? { price_change_percent: input.priceChangePercent } : {}),
      ...(input.priceChangeAt ? { price_change_at: input.priceChangeAt } : {}),
      ...(input.isBestStackBedroom !== undefined && input.isBestStackBedroom !== null ? { is_best_stack_bedroom: input.isBestStackBedroom } : {}),
    }
  );
}

export function logPricePromoChartOpened(input: {
  unitId: string | number;
  chartType: 'price' | 'psf';
  showOnlyDrop?: boolean;
  source: string;
  ceaNumber?: string | null;
}): void {
  safeLogEvent('price_promo_chart_opened', {
    unit_id: String(input.unitId),
    chart_type: input.chartType,
    source: input.source,
    ...(input.showOnlyDrop !== undefined ? { show_only_drop: input.showOnlyDrop } : {}),
    ...(input.ceaNumber ? { cea_number: input.ceaNumber } : {}),
  });
}

export function logPricePromoEarlyAccessOpened(input: {
  source: string;
  ceaNumber?: string | null;
}): void {
  safeLogEvent('price_promo_early_access_opened', {
    source: input.source,
    ...(input.ceaNumber ? { cea_number: input.ceaNumber } : {}),
  });
}

export function logPricePromoEarlyAccessRegister(input: {
  source: string;
  ceaNumber?: string | null;
}): void {
  safeLogEvent('price_promo_early_access_register', {
    source: input.source,
    ...(input.ceaNumber ? { cea_number: input.ceaNumber } : {}),
  });
}

export function logFactsheetViewed(
  projectName?: string | null,
  ceaNumber?: string | null,
  projectId?: string | number | null
): void {
  safeLogEvent(
    'factsheet_viewed',
    toTrackingBaseParams({
      projectName,
      projectId,
      source: 'factsheet',
      ceaNumber,
    })
  );
}

/**
 * Log the creation of a public share link from `ShareExternalUserDialog`.
 * Captures the issuing agent (CEA), the recipient's mobile, the generated
 * share uuid, and the module being shared so we can attribute downstream
 * recipient activity back to this invite.
 *
 * `ceaNumber` falls back to `getCachedAnalyticsCea()` when not supplied so
 * call sites without easy access to user context still emit a tagged event.
 */
export function logShareLinkCreated(input: {
  module: string;
  /** Recipient mobile as entered by the agent (digits + any formatting). */
  mobile: string;
  /** Backend-issued share uuid (the `?uuid=` the recipient lands on). */
  uuid: string;
  ceaNumber?: string | null;
  projectId?: string | number | null;
}): void {
  const cea = input.ceaNumber?.trim() || getCachedAnalyticsCea();
  safeLogEvent('share_link_created', {
    module: input.module,
    recipient_mobile: input.mobile,
    share_uuid: input.uuid,
    ...(input.projectId !== undefined && input.projectId !== null
      ? { project_id: String(input.projectId) }
      : {}),
    ...(cea ? { cea_number: cea } : {}),
  });
}

/**
/**
 * Log the viewing of the Marketing Materials Tab.
 */
export function logMarketingMaterialsTabViewed(ceaNumber?: string | null): void {
  safeLogEvent('marketing_materials_tab_viewed', {
    ...(ceaNumber ? { cea_number: ceaNumber } : {}),
  });
}

/**
 * Log the preview of a project details file (EDM or eBook).
 */
export function logProjectDetailsPreviewed(
  type: 'edm' | 'ebook',
  projectId?: string | number | null,
  fileId?: string | number | null,
  ceaNumber?: string | null
): void {
  const eventName = type === 'edm' ? 'project_details_edm_previewed' : 'project_details_ebook_previewed';
  safeLogEvent(eventName, {
    ...(projectId !== undefined && projectId !== null ? { project_id: String(projectId) } : {}),
    ...(fileId !== undefined && fileId !== null ? { file_id: String(fileId) } : {}),
    ...(ceaNumber ? { cea_number: ceaNumber } : {}),
  });
}

/**
 * Log the downloading of a project details file (EDM or eBook).
 */
export function logProjectDetailsDownloaded(
  type: 'edm' | 'ebook',
  projectId?: string | number | null,
  fileId?: string | number | null,
  ceaNumber?: string | null
): void {
  const eventName = type === 'edm' ? 'project_details_edm_downloaded' : 'project_details_ebook_downloaded';
  safeLogEvent(eventName, {
    ...(projectId !== undefined && projectId !== null ? { project_id: String(projectId) } : {}),
    ...(fileId !== undefined && fileId !== null ? { file_id: String(fileId) } : {}),
    ...(ceaNumber ? { cea_number: ceaNumber } : {}),
  });
}

/**
 * Log thumbs feedback on a Super Picker AI assistant message. GA4 is the
 * dashboard convenience copy — the DB (TransformationChatMessage) is the
 * source of truth for the weekly triage loop.
 */
export function logSuperPickerChatFeedback(input: {
  feedback: 'up' | 'down';
  reason?: string | null;
  conversationId?: number | null;
  messageId?: number | null;
  ceaNumber?: string | null;
}): void {
  const cea = input.ceaNumber?.trim() || getCachedAnalyticsCea();
  safeLogEvent('super_picker_chat_feedback', {
    feedback: input.feedback,
    ...(input.reason ? { reason: input.reason } : {}),
    ...(input.conversationId != null ? { conversation_id: String(input.conversationId) } : {}),
    ...(input.messageId != null ? { message_id: String(input.messageId) } : {}),
    ...(cea ? { cea_number: cea } : {}),
  });
}
  
/**
 * Super Showcase: the gallery was opened from the sidebar, and which
 * showcase image the agent then stepped into.
 */
export function logSuperShowcaseOpened(input: {
  source: string;
  totalShowcases?: number;
  ceaNumber?: string | null;
}): void {
  safeLogEvent('super_showcase_opened', {
    source: input.source,
    ...(input.totalShowcases !== undefined ? { total_showcases: input.totalShowcases } : {}),
    ...(input.ceaNumber ? { cea_number: input.ceaNumber } : {}),
  });
}

export function logSuperShowcaseViewed(input: {
  showcaseId: number;
  title: string;
  hotspotCount: number;
  ceaNumber?: string | null;
}): void {
  safeLogEvent('super_showcase_viewed', {
    showcase_id: String(input.showcaseId),
    showcase_title: input.title,
    hotspot_count: input.hotspotCount,
    ...(input.ceaNumber ? { cea_number: input.ceaNumber } : {}),
  });
}

/** An agent shared a showcase link (copy, WhatsApp or the native share sheet). */
export function logSuperShowcaseShared(input: {
  showcaseId: number;
  title: string;
  method: 'copy' | 'whatsapp' | 'native';
  source: 'gallery' | 'viewer';
  ceaNumber?: string | null;
}): void {
  safeLogEvent('super_showcase_shared', {
    showcase_id: String(input.showcaseId),
    showcase_title: input.title,
    method: input.method,
    source: input.source,
    ...(input.ceaNumber ? { cea_number: input.ceaNumber } : {}),
  });
}

/** A clickable area was tapped, and which function it routed to. */
export function logSuperShowcaseHotspotClicked(input: {
  showcaseId: number;
  showcaseTitle: string;
  hotspotLabel: string;
  actionType: string;
  ceaNumber?: string | null;
}): void {
  safeLogEvent('super_showcase_hotspot_clicked', {
    showcase_id: String(input.showcaseId),
    showcase_title: input.showcaseTitle,
    hotspot_label: input.hotspotLabel,
    action_type: input.actionType,
    ...(input.ceaNumber ? { cea_number: input.ceaNumber } : {}),
  });
}

export function logBestPricePromoShared(input: {
  /** 'preview' when the sheet is generated, 'download' when the image is saved. */
  stage: 'preview' | 'download';
  totalProjects: number;
  pageCount?: number;
  topSaving?: number;
  ceaNumber?: string | null;
}): void {
  safeLogEvent('best_price_promo_shared', {
    stage: input.stage,
    total_projects: input.totalProjects,
    ...(input.pageCount !== undefined ? { page_count: input.pageCount } : {}),
    ...(input.topSaving !== undefined ? { top_saving: input.topSaving } : {}),
    ...(input.ceaNumber ? { cea_number: input.ceaNumber } : {}),
  });
}

/**
 * My Shareable Links. Every event carries the agent's CEA (from the cached
 * session value, since the tab's components don't receive it) so usage can be
 * broken down per agent.
 */
type SharableLinkRef = {
  id: number;
  linkType: string;
  projectId: string | null;
  projectName: string | null;
};

function sharableLinkParams(link: SharableLinkRef): Record<string, string | number | boolean> {
  return {
    link_id: String(link.id),
    link_type: link.linkType,
    ...(link.projectId ? { project_id: link.projectId } : {}),
    ...(link.projectName ? { project_name: link.projectName } : {}),
  };
}

function withCachedCea(params: Record<string, string | number | boolean>): Record<string, string | number | boolean> {
  const cea = getCachedAnalyticsCea();
  return cea ? { ...params, cea_number: cea } : params;
}

/** The tab finished its first load of the agent's links. */
export function logSharableLinksViewed(input: {
  source: 'tab' | 'deep_link';
  linkCount: number;
  liveLinkCount: number;
  totalViews: number;
  totalLeads: number;
}): void {
  safeLogEvent('sharable_links_viewed', withCachedCea({
    source: input.source,
    link_count: input.linkCount,
    live_link_count: input.liveLinkCount,
    total_views: input.totalViews,
    total_leads: input.totalLeads,
  }));
}

/** The agent pulled the launch page down. `trigger` is what rolled it. */
export function logSharableLinksIntroOpened(input: { trigger: 'banner' | 'roller' | 'auto' }): void {
  safeLogEvent('sharable_links_intro_opened', withCachedCea({ trigger: input.trigger }));
}

/** First time per opening that the launch page was scrolled past 25 / 50 / 75 / 100 %. */
export function logSharableLinksIntroScrollDepth(input: { percent: 25 | 50 | 75 | 100 }): void {
  safeLogEvent('sharable_links_intro_scroll_depth', withCachedCea({ percent: input.percent }));
}

/** A link or button inside the launch page was clicked. */
export function logSharableLinksIntroCtaClick(input: { label: string; action: 'create_link' | 'close' | 'external' }): void {
  safeLogEvent('sharable_links_intro_cta_click', withCachedCea({
    label: compactString(input.label, 100) ?? '',
    action: input.action,
  }));
}

/** The launch page was rolled back up (or left open when the tab unmounted). */
export function logSharableLinksIntroClosed(input: {
  trigger: 'end_reached' | 'escape' | 'roller' | 'banner' | 'cta' | 'unmount';
  durationMs: number;
  maxScrollPercent: number;
}): void {
  safeLogEvent('sharable_links_intro_closed', withCachedCea({
    trigger: input.trigger,
    duration_sec: Math.round(input.durationMs / 1000),
    max_scroll_percent: input.maxScrollPercent,
    reached_end: input.maxScrollPercent >= 100,
  }));
}

/** The create-link dialog was opened. */
export function logSharableLinkCreateOpened(input: { source: 'stats' | 'empty_state' | 'intro' | 'project_header' }): void {
  safeLogEvent('sharable_link_create_opened', withCachedCea({ source: input.source }));
}

/** A new link was created from My Shareable Links. */
export function logSharableLinkCreated(input: {
  link: SharableLinkRef;
  includeSunlight: boolean;
  hasConnectorTag: boolean;
  descriptionLength: number;
  showDesignation: boolean;
  hasCustomTitle: boolean;
}): void {
  safeLogEvent('sharable_link_created', withCachedCea({
    ...sharableLinkParams(input.link),
    include_sunlight: input.includeSunlight,
    show_designation: input.showDesignation,
    has_custom_title: input.hasCustomTitle,
    has_connector_tag: input.hasConnectorTag,
    description_length: input.descriptionLength,
  }));
}

export function logSharableLinkCreateFailed(input: { linkType: string; projectId?: string | null; error: string }): void {
  safeLogEvent('sharable_link_create_failed', withCachedCea({
    link_type: input.linkType,
    ...(input.projectId ? { project_id: input.projectId } : {}),
    error: compactString(input.error, 100) ?? 'unknown',
  }));
}

/** The create dialog was closed before a link was made; `step` is where the agent gave up. */
export function logSharableLinkCreateAbandoned(input: { step: 'project' | 'customize' | 'profile'; hasProject: boolean }): void {
  safeLogEvent('sharable_link_create_abandoned', withCachedCea({
    step: input.step,
    has_project: input.hasProject,
  }));
}

export type SharableLinkAction =
  | 'copy'
  | 'whatsapp'
  | 'preview'
  | 'qr_open'
  | 'qr_download'
  | 'media_card_view'
  | 'media_card_download'
  | 'revoke'
  | 'remove'
  | 'config_open'
  | 'config_save'
  | 'leads_open'
  | 'lead_open_connector'
  | 'lead_whatsapp'
  | 'lead_deny_access'
  | 'lead_restore_access'
  | 'clients_open'
  | 'client_whatsapp';

/**
 * One event for every action on an existing link, broken down by `action`.
 * `source` is where it was taken: the link card, its ⋮ menu, the "link is
 * ready" dialog, the leads drawer, or the "Send to my clients" sheet.
 */
export function logSharableLinkAction(input: {
  action: SharableLinkAction;
  link: SharableLinkRef;
  source: 'card' | 'menu' | 'done_dialog' | 'leads_drawer' | 'config_dialog' | 'client_sheet';
  leadCount?: number;
}): void {
  safeLogEvent('sharable_link_action', withCachedCea({
    action: input.action,
    source: input.source,
    ...sharableLinkParams(input.link),
    ...(input.leadCount !== undefined ? { lead_count: input.leadCount } : {}),
  }));
}
